import React from 'react';
import { Pill } from '@/components/ui/Pill';
import { LazySection } from '@/components/ui/LazySection';
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

interface ProviderActiveFiltersProps {
  selectedCountries: string[];
  setSelectedCountries: (v: string[]) => void;
  selectedTechnologies: string[];
  setSelectedTechnologies: (v: string[]) => void;
  selectedProductTypes: string[];
  setSelectedProductTypes: (v: string[]) => void;
  selectedPlatforms: string[];
  setSelectedPlatforms: (v: string[]) => void;
  technologyOptions: { id: string; name: string }[];
  productTypeOptions: { id: string; name: string }[];
  platformOptions: { id: string; name: string }[];
  onClearFilters: () => void;
  viewMode: 'card' | 'list';
  setViewMode: (v: 'card' | 'list') => void;
  sortBy: string;
  setSortBy: (v: string) => void;
}

export default function ProviderActiveFilters(props: ProviderActiveFiltersProps) {
  const anyActive = [
    props.selectedCountries.length,
    props.selectedTechnologies.length,
    props.selectedProductTypes.length,
    props.selectedPlatforms.length,
  ].some(Boolean);

  // Sort options
  const sortOptions = [
    { value: "name-asc", label: "Sort by Name (A-Z)" },
    { value: "name-desc", label: "Sort by Name (Z-A)" },
    { value: "location-asc", label: "Sort by Location (A-Z)" },
    { value: "location-desc", label: "Sort by Location (Z-A)" },
  ];

  const currentSortLabel = sortOptions.find(opt => opt.value === props.sortBy)?.label.replace("Sort by", "Sorted by") || "Sort by...";
  const [sortOpen, setSortOpen] = React.useState(false);

  return (
    <LazySection minHeight={40}>
      {/* Filter Pills - Between filters and sort controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {props.selectedCountries.map((country) => (
          <Pill key={country} type="selectedFilterCountry">
            {country}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove country filter" onClick={() => props.setSelectedCountries(props.selectedCountries.filter((c) => c !== country))}>×</button>
          </Pill>
        ))}
        {props.selectedTechnologies.map((tech) => (
          <Pill key={tech} type="selectedFilterTechnology">
            {props.technologyOptions.find(opt => opt.id === tech)?.name || tech}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove technology filter" onClick={() => props.setSelectedTechnologies(props.selectedTechnologies.filter((t) => t !== tech))}>×</button>
          </Pill>
        ))}
        {props.selectedProductTypes.map((type) => (
          <Pill key={type} type="selectedFilterProductType">
            {props.productTypeOptions.find(opt => opt.id === type)?.name || type}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove product type filter" onClick={() => props.setSelectedProductTypes(props.selectedProductTypes.filter((t) => t !== type))}>×</button>
          </Pill>
        ))}
        {props.selectedPlatforms.map((platform) => (
          <Pill key={platform} type="selectedFilterPlatform">
            {props.platformOptions.find(opt => opt.id === platform)?.name || platform}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove platform filter" onClick={() => props.setSelectedPlatforms(props.selectedPlatforms.filter((p) => p !== platform))}>×</button>
          </Pill>
        ))}
      </div>

      {/* Sort and View Controls Row */}
      <div className="flex w-full mb-4 items-start justify-between gap-4">
        {/* Left Side - Sort Dropdown */}
        <div className="flex items-center gap-2">
          <Popover open={sortOpen} onOpenChange={setSortOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={sortOpen}
                aria-label="Select sort option"
                className={cn(
                  "w-64 h-10 justify-between border-0 font-sans text-base transition-colors bg-neutral-500 text-white hover:bg-neutral-600 focus:bg-neutral-600 [&_svg]:text-white"
                )}
              >
                <span className="flex-1 text-left">{currentSortLabel}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0 bg-white rounded-lg shadow-lg border border-neutral-200 font-sans text-neutral-900 z-50">
              <Command>
                <CommandEmpty>No sort options found.</CommandEmpty>
                <CommandGroup>
                  {sortOptions.filter(opt => opt.value !== props.sortBy).map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      onSelect={() => {
                        props.setSortBy(opt.value);
                        setSortOpen(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:!bg-neutral-200 transition-colors data-[selected=true]:!bg-neutral-200"
                    >
                      <span className="flex-1">{opt.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Right Side - Clear Filters Button and View Mode Toggle */}
        <div className="flex items-center gap-2">
          {/* Clear Filters Button */}
          {anyActive && (
            <button type="button" className="bg-neutral-200 text-neutral-800 rounded-md px-4 py-2 text-sm font-sans font-medium hover:bg-neutral-300 transition-colors whitespace-nowrap" onClick={props.onClearFilters}>Clear Filters</button>
          )}
          <button type="button" aria-label="List View" aria-pressed={props.viewMode === 'list'} onClick={() => props.setViewMode('list')} className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors border-none focus:outline-none ${props.viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-500'}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="6" width="24" height="2" rx="1" fill="currentColor" /><rect y="11" width="24" height="2" rx="1" fill="currentColor" /><rect y="16" width="24" height="2" rx="1" fill="currentColor" /></svg>
          </button>
          <button type="button" aria-label="Card/Grid View" aria-pressed={props.viewMode === 'card'} onClick={() => props.setViewMode('card')} className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors border-none focus:outline-none ${props.viewMode === 'card' ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-500'}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor" /><rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" /><rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" /><rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" /></svg>
          </button>
        </div>
      </div>
    </LazySection>
  );
} 